import { Link, useNavigate } from 'react-router'
import { FilterCombobox } from '#app/components/filter-combobox.tsx'
import { Icon } from '#app/components/ui/icon.tsx'

/**
 * Filtering a ranking by what reviewers talk about.
 *
 * This was a wall of pills on both ranking pages — 85 of them on metro
 * roofing — laid down the page before the ranking they were meant to refine. A
 * list that long is not a filter, it is a second navigation the reader has to
 * read in full before they can use it.
 *
 * A searchable control inverts that: options are a keystroke away, and only
 * applied filters take space. Selections stack, so a reader narrows rather
 * than swapping one theme for another.
 *
 * Shared by /best/{category} and /{city}/{category} rather than copied into
 * each. The two ranking pages have drifted repeatedly — different score
 * marks, different table padding, different phone formatting — and every one
 * of those started as the same markup pasted twice.
 *
 * Plain links, not client state. Each chip is an anchor to the same URL minus
 * that mention, so filtering works without JavaScript, every state is
 * addressable, and the back button behaves.
 *
 * ## Two vocabularies, on purpose
 *
 * Internally these are `themes` — the column on Business, the output of the
 * synthesis prompt, the shape review-synthesis normalizes and groups. That is
 * an accurate engineering name and it stays.
 *
 * Readers are never shown it. To someone choosing a dentist these are simply
 * the things reviewers keep mentioning, so every string here says that. Do not
 * "fix" one vocabulary to match the other: the data model should describe the
 * data, and the interface should describe what the reader is looking at.
 *
 * The pipeline strip in the dashboard calls them "review topics", a third
 * wording for a third audience. An operator needs a name that is neither the
 * column nor the sales copy, and "themes" there collides with the light/dark
 * theme this app also has.
 */

export interface ThemeFacet {
	slug: string
	label: string
	count: number
}

export function ThemeFilter({
	basePath,
	facets,
	active,
}: {
	/** Path the filters apply to, without a query string. */
	basePath: string
	facets: ThemeFacet[]
	active: ThemeFacet[]
}) {
	const navigate = useNavigate()
	const activeSlugs = new Set(active.map((f) => f.slug))

	const hrefWith = (slugs: string[]) => {
		const params = new URLSearchParams()
		// `mentions`, not `theme`. The param is reader-facing — it shows in the
		// address bar and in shared links — so it follows the reader vocabulary
		// below, and both ranking loaders read it under this name. These drifted
		// apart once already, which left the control navigating without ever
		// narrowing anything.
		for (const slug of slugs) params.append('mentions', slug)
		const query = params.toString()
		return `${basePath}${query ? `?${query}` : ''}`
	}

	const unselected = facets.filter((facet) => !activeSlugs.has(facet.slug))

	return (
		<div className="mb-8 flex flex-wrap items-center gap-2">
			<FilterCombobox
				emptyText="Nothing matches"
				onValueChange={(value) =>
					void navigate(hrefWith([...activeSlugs, value]))
				}
				options={unselected.map((facet) => ({
					label: `${facet.label} (${facet.count})`,
					value: facet.slug,
				}))}
				placeholder={
					active.length ? 'Add another' : 'Filter by what reviewers mention'
				}
				searchPlaceholder="Search what reviewers mention..."
			/>

			{active.map((facet) => (
				<Link
					key={facet.slug}
					className="bg-secondary text-secondary-foreground hover:bg-secondary/70 inline-flex items-center gap-1.5 rounded-full py-1 pr-2 pl-3 text-sm transition-colors"
					to={hrefWith(
						active.filter((f) => f.slug !== facet.slug).map((f) => f.slug),
					)}
					aria-label={`Remove ${facet.label} filter`}
				>
					{facet.label}
					<Icon name="cross-1" className="size-3" />
				</Link>
			))}

			{active.length > 1 ? (
				<Link
					className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-4"
					to={basePath}
				>
					Clear all
				</Link>
			) : null}
		</div>
	)
}
